// A Boo that runs on the org's own machine. Kept free of the `@/lib/api`
// import chain so it can be unit-tested under `node:test`.

// containers.hostingTarget for a self-hosted Boo.
export const SELF_HOSTED = "self-hosted";

const DATA_ROOT = "/data";
const LOCAL_URL = "http://localhost:3000";
const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]"]);

// What create returns that the env file needs (SelfHostedBooToken in lib/api.ts).
export type IssuedSelfHostedBoo = {
  id: string;
  name: string;
  token: string;
  integrationsAddr: string;
  brainUrl: string | null;
};

// 32 random bytes, base64: the shape BOO_DEFAULT_VAULT_MASTER_KEY expects.
// Made in this browser so the key that seals the Boo's stored secrets never
// reaches Boo Lab.
export function vaultMasterKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes));
}

// An unquoted value that `docker --env-file` and dotenv-style loaders read
// the same: one line, no quotes, `#`, `$` or backslash.
export function envValue(text: string): string {
  return text.replace(/\p{Cc}+/gu, " ").replace(/["'`#$\\]/g, "").replace(/\s+/g, " ").trim();
}

export function selfHostedEnv(issued: IssuedSelfHostedBoo, masterKey: string = vaultMasterKey()): string {
  const lines = [
    `# Self-hosted Boo ${issued.id}. Keep this file private.`,
    `BOO_INTEGRATIONS_ADDR=${issued.integrationsAddr}`,
    `BOO_INTEGRATIONS_TOKEN=${issued.token}`,
    `BOO_BACKEND_URL=${issued.integrationsAddr}`,
    `BOO_CONTAINER_ID=${issued.id}`,
    `AGENT_NAME=${envValue(issued.name) || "Boo"}`,
    `BOO_TRACING=filesystem`,
    `BOO_STORAGE_BACKEND=sqlite`,
    `BOO_DATA_ROOT=${DATA_ROOT}`,
    `BOO_DEFAULT_VAULT_ROOT=${DATA_ROOT}/default-vault`,
    `# Made in your browser and never sent to Boo Lab. Losing it loses this Boo's stored secrets.`,
    `BOO_DEFAULT_VAULT_MASTER_KEY=${masterKey}`,
    `BOO_DEFAULT_VAULT_PUBLIC_URL=${issued.brainUrl ?? LOCAL_URL}`,
  ];
  if (issued.brainUrl) lines.push(`BOO_BRAIN_URL=${issued.brainUrl}`);
  return lines.join("\n");
}

// The same rule as the backend: an origin, https, or http on this machine.
export function parseBrainUrl(raw: string): { ok: true; value: string | null } | { ok: false } {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false };
  }
  const secure = url.protocol === "https:" || (url.protocol === "http:" && LOOPBACK.has(url.hostname));
  if (!secure || url.pathname !== "/" || url.search || url.hash) return { ok: false };
  return { ok: true, value: url.origin };
}
